import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { deleteJSON } from '@/lib/api'
import { browserRequestId } from '@/lib/browserRequestId'
import { formatMoneyCents } from '@/lib/money'
import { MemberStatusSources } from '@/components/member-portal/MemberStatusSources'
import {
  displayPortalDate,
  type MemberPortalMembership,
  type MemberStatusSource,
} from '@/components/member-portal/memberPortalPresentation'

export type { MemberPortalMembership } from '@/components/member-portal/memberPortalPresentation'

export function MemberPortalOverview({
  associationName,
  membership,
  person,
  openInvoices = [],
  currency = 'USD',
  memberStatusSources = [],
}: {
  associationName: string
  membership?: MemberPortalMembership | null
  person?: { name: string; email: string; emailSuppressed?: boolean }
  openInvoices?: Array<{
    id: number
    number: number
    origin: string
    due_on: string
    balance: string
    balance_cents: number
  }>
  currency?: string
  memberStatusSources?: MemberStatusSource[]
}) {
  const rosterAccess = activeRosterOrganisations(memberStatusSources)
  return (
    <div className="mt-6 space-y-6">
      <AttentionBand person={person} openInvoices={openInvoices} currency={currency} />
      <ScheduledCancellationNotice membership={membership} />
      {memberStatusSources.length > 0 ? (
        <MemberStatusSources sources={memberStatusSources} />
      ) : null}
      <OverviewSection heading="Membership">
        <Card className="flex items-center justify-between gap-5 p-4">
          {membership ? (
            <>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-body font-medium">{membership.level}</p>
                  <Badge variant={membership.status === 'Active' ? 'success' : 'neutral'}>
                    {membership.status}
                  </Badge>
                </div>
                <p className="mt-1 text-caption text-muted-foreground">
                  {membershipCaption(membership)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-body-sm font-medium tabular-nums">
                  {membership.yearly_price_label ?? membership.yearly_price}/year
                </p>
                {membership.renewal_due_on && (
                  <p className="mt-1 text-caption text-muted-foreground">
                    Renews {displayPortalDate(membership.renewal_due_on)}
                  </p>
                )}
              </div>
            </>
          ) : rosterAccess.length > 0 ? (
            <p className="text-body-sm text-muted-foreground">
              {organisationAccessSentence(rosterAccess)}
            </p>
          ) : (
            <>
              <p className="text-body-sm text-muted-foreground">Not a member</p>
              <Button asChild variant="outline">
                <a href="/join">Join {associationName}</a>
              </Button>
            </>
          )}
        </Card>
      </OverviewSection>

      {/* {membership && (the Membership summary is present) */}
      {membership && membership.status !== 'Pending-New' && (
        <OverviewSection heading="Autopay">
          <Card className="p-4">
            {membership.autopay.enrolled ? (
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                  <CreditCard className="size-4" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-body-sm font-medium">
                    {membership.autopay.brand} •••• {membership.autopay.last4}
                  </p>
                  {membership.autopay.next_charge_on && (
                    <p className="text-caption text-muted-foreground">
                      Next charge {displayPortalDate(membership.autopay.next_charge_on)}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-caption text-muted-foreground">Autopay is off</p>
            )}
          </Card>
        </OverviewSection>
      )}
    </div>
  )
}

function AttentionBand({
  person,
  openInvoices,
  currency,
}: {
  person?: { email: string; emailSuppressed?: boolean }
  openInvoices: Array<{ id: number; balance_cents: number }>
  currency: string
}) {
  if (openInvoices.length === 0 && !person?.emailSuppressed) return null

  return (
    <section className="space-y-3" aria-label="Needs attention">
      {openInvoices.length > 0 && <BalanceDueCard invoices={openInvoices} currency={currency} />}
      {person?.emailSuppressed && <SuppressedEmailBanner email={person.email} />}
    </section>
  )
}

function BalanceDueCard({
  invoices,
  currency,
}: {
  invoices: Array<{ id: number; balance_cents: number }>
  currency: string
}) {
  const balanceCents = invoices.reduce((total, invoice) => total + invoice.balance_cents, 0)
  return (
    <Card className="flex items-center justify-between gap-4 p-4">
      <div>
        <p className="text-caption text-muted-foreground">Balance due</p>
        <p className="text-heading-sm font-semibold tabular-nums">
          {formatMoney(balanceCents, currency)}
        </p>
        <p className="text-caption text-muted-foreground">
          {invoices.length} open {invoices.length === 1 ? 'invoice' : 'invoices'}
        </p>
      </div>
      <Button asChild type="button">
        <Link to={`/portal/billing?select=${invoices.map((invoice) => invoice.id).join(',')}`}>
          Pay {formatMoney(balanceCents, currency)}
        </Link>
      </Button>
    </Card>
  )
}

function SuppressedEmailBanner({ email }: { email: string }) {
  return (
    <Alert className="flex items-center justify-between gap-4 px-4 py-3">
      <span>We can&apos;t deliver email to {email}. Update your email address.</span>
      <Button asChild type="button" variant="outline" size="sm" className="shrink-0">
        <Link to="/portal/profile?edit=email">Update your email address</Link>
      </Button>
    </Alert>
  )
}

function formatMoney(cents: number, currency: string) {
  return formatMoneyCents(cents, currency)
}

function activeRosterOrganisations(sources: MemberStatusSource[]) {
  return sources
    .filter((source) => source.kind === 'organization_roster' && source.status === 'Active')
    .map((source) => source.label)
}

function organisationAccessSentence(names: string[]) {
  const noun = names.length === 1 ? 'organisation' : 'organisations'
  const listed =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
  return `You have active member access through your ${noun}, ${listed}.`
}

function ScheduledCancellationNotice({
  membership,
}: {
  membership?: MemberPortalMembership | null
}) {
  const [scheduledEndOn, setScheduledEndOn] = React.useState(
    membership?.status === 'Lapsed' ? null : (membership?.scheduled_cancellation_end_on ?? null),
  )
  const queryClient = useQueryClient()
  const undoKey = React.useRef(`portal-cancellation-undo-${browserRequestId()}`)
  const undo = useMutation({
    mutationFn: () =>
      deleteJSON('/api/v1/member_portal/cancellation', {
        cancellation: { idempotency_key: undoKey.current },
      }),
    onSuccess: () => {
      setScheduledEndOn(null)
      queryClient.invalidateQueries({ queryKey: ['member-portal-session'] })
    },
  })

  React.useEffect(() => {
    setScheduledEndOn(
      membership?.status === 'Lapsed' ? null : (membership?.scheduled_cancellation_end_on ?? null),
    )
  }, [membership?.scheduled_cancellation_end_on, membership?.status])

  if (!scheduledEndOn) return null

  return (
    <Alert
      variant="warning"
      className="flex items-center justify-between gap-4 border-border bg-card px-4 py-3 text-foreground"
    >
      <span>Membership cancels on {displayPortalDate(scheduledEndOn)}.</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => undo.mutate()}
        disabled={undo.isPending}
      >
        {undo.isPending ? 'Undoing...' : 'Undo'}
      </Button>
    </Alert>
  )
}

function OverviewSection({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-body font-semibold">{heading}</h2>
      {children}
    </section>
  )
}

function membershipCaption(membership: MemberPortalMembership) {
  const number = membership.member_number ? `Member #${membership.member_number}` : null
  const since = membership.member_since_on
    ? `since ${displayPortalDate(membership.member_since_on)}`
    : null
  return [number, since].filter(Boolean).join(' · ')
}
